import { useState } from 'react'
import Header from '@/components/Header'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Trash2, Plus, Edit, Loader2, MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useEntryPassTemplates } from '@/hooks/use-entry-pass-templates'
import {
  EntryPassTemplate,
  EntryPassTemplateFormData,
  DayOfWeek,
  TimeSlot
} from '@/constants'

const DAYS_OF_WEEK: { value: DayOfWeek; label: string }[] = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
]

const TIME_SLOTS: { value: TimeSlot; label: string; defaultStart: string; defaultEnd: string }[] = [
  { value: 'daytime', label: 'Daytime', defaultStart: '11:00', defaultEnd: '17:00' },
  { value: 'evening', label: 'Evening', defaultStart: '18:00', defaultEnd: '02:00' }
]

const getDefaultFormData = (): EntryPassTemplateFormData => ({
  name: '',
  dayOfWeek: 1,
  timeSlot: 'evening',
  startTime: '18:00',
  endTime: '02:00',
  priceCouples: 0,
  priceSingleLadies: 0,
  priceSingleMales: 0,
  isActive: true
})

const EntryPassTemplatesPage: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<EntryPassTemplate | null>(null)
  const [formData, setFormData] = useState<EntryPassTemplateFormData>(getDefaultFormData())

  const {
    templates,
    isLoading,
    isSubmitting,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    toggleActive
  } = useEntryPassTemplates()

  const resetForm = () => {
    setFormData(getDefaultFormData())
  }

  const handleCreate = () => {
    resetForm()
    setIsCreateDialogOpen(true)
  }

  const handleEdit = (template: EntryPassTemplate) => {
    setEditingTemplate(template)
    setFormData({
      name: template.name,
      dayOfWeek: template.dayOfWeek,
      timeSlot: template.timeSlot,
      startTime: template.startTime,
      endTime: template.endTime,
      priceCouples: parseFloat(template.priceCouples) || 0,
      priceSingleLadies: parseFloat(template.priceSingleLadies) || 0,
      priceSingleMales: parseFloat(template.priceSingleMales) || 0,
      isActive: template.isActive
    })
    setIsEditDialogOpen(true)
  }

  const handleSubmit = async (isEdit: boolean = false) => {
    if (!formData.name) {
      return
    }

    let success: boolean
    if (isEdit && editingTemplate) {
      success = await updateTemplate(editingTemplate.id, formData)
    } else {
      success = await createTemplate(formData)
    }

    if (success) {
      setIsCreateDialogOpen(false)
      setIsEditDialogOpen(false)
      setEditingTemplate(null)
      resetForm()
    }
  }

  const handleDelete = async (template: EntryPassTemplate) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return
    }
    await deleteTemplate(template.id)
  }

  const handleToggleActive = async (template: EntryPassTemplate) => {
    await toggleActive(template.id, !template.isActive)
  }

  const handleTimeSlotChange = (timeSlot: TimeSlot) => {
    const slot = TIME_SLOTS.find(s => s.value === timeSlot)
    if (slot) {
      setFormData(prev => ({
        ...prev,
        timeSlot,
        startTime: slot.defaultStart,
        endTime: slot.defaultEnd
      }))
    }
  }

  const getDayLabel = (day: DayOfWeek) => {
    return DAYS_OF_WEEK.find(d => d.value === day)?.label || ''
  }

  const formatPrice = (price: string | number) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    return numPrice === 0 ? 'FREE' : `$${numPrice}`
  }

  const formatTimeRange = (startTime: string, endTime: string) => {
    const formatTime = (time: string) => {
      const [hour, minute] = time.split(':').map(Number)
      const date = new Date(2000, 0, 1, hour, minute)
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    }
    return `${formatTime(startTime)} - ${formatTime(endTime)}`
  }

  const renderTemplateForm = (isEdit: boolean) => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div>
        <Label htmlFor="name">Template Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Monday Evening"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="dayOfWeek">Day of Week *</Label>
          <Select
            value={formData.dayOfWeek.toString()}
            onValueChange={(value) => setFormData(prev => ({ ...prev, dayOfWeek: parseInt(value) as DayOfWeek }))}
          >
            <SelectTrigger className="text-foreground">
              <SelectValue placeholder="Select day" />
            </SelectTrigger>
            <SelectContent>
              {DAYS_OF_WEEK.map(day => (
                <SelectItem key={day.value} value={day.value.toString()} className="data-[state=checked]:text-foreground">
                  {day.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="timeSlot">Time Slot *</Label>
          <Select
            value={formData.timeSlot}
            onValueChange={(value) => handleTimeSlotChange(value as TimeSlot)}
          >
            <SelectTrigger className="text-foreground">
              <SelectValue placeholder="Select time slot" />
            </SelectTrigger>
            <SelectContent>
              {TIME_SLOTS.map(slot => (
                <SelectItem key={slot.value} value={slot.value} className="data-[state=checked]:text-foreground">
                  {slot.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="startTime">Start Time</Label>
          <Input
            id="startTime"
            type="time"
            value={formData.startTime}
            onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
          />
        </div>

        <div>
          <Label htmlFor="endTime">End Time</Label>
          <Input
            id="endTime"
            type="time"
            value={formData.endTime}
            onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
          />
        </div>
      </div>

      <div className="space-y-3">
        <Label>Pricing (USD)</Label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="priceCouples" className="text-xs text-muted-foreground">Couples</Label>
            <Input
              id="priceCouples"
              type="number"
              min="0"
              value={formData.priceCouples}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                priceCouples: parseFloat(e.target.value) || 0
              }))}
            />
          </div>
          <div>
            <Label htmlFor="priceSingleLadies" className="text-xs text-muted-foreground">Single Ladies</Label>
            <Input
              id="priceSingleLadies"
              type="number"
              min="0"
              value={formData.priceSingleLadies}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                priceSingleLadies: parseFloat(e.target.value) || 0
              }))}
            />
          </div>
          <div>
            <Label htmlFor="priceSingleMales" className="text-xs text-muted-foreground">Single Males</Label>
            <Input
              id="priceSingleMales"
              type="number"
              min="0"
              value={formData.priceSingleMales}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                priceSingleMales: parseFloat(e.target.value) || 0
              }))}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="isActive"
          checked={formData.isActive}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
        />
        <Label htmlFor="isActive">Active</Label>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button
          variant="outline"
          onClick={() => {
            setIsCreateDialogOpen(false)
            setIsEditDialogOpen(false)
            setEditingTemplate(null)
            resetForm()
          }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleSubmit(isEdit)}
          disabled={isSubmitting || !formData.name}
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </div>
  )

  return (
    <>
      <Header />
      <div className="max-w-5xl mx-auto px-5 pb-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">Entry Pass Templates</h1>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Entry Pass Template</DialogTitle>
              </DialogHeader>
              {renderTemplateForm(false)}
            </DialogContent>
          </Dialog>
        </div>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Templates</h2>

          {error ? (
            <div className="text-destructive">Failed to load templates</div>
          ) : isLoading ? (
            <div className="text-muted-foreground">Loading templates...</div>
          ) : !templates.length ? (
            <div className="text-muted-foreground">No templates configured. Click "Add Template" to create one.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Day</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Couples</TableHead>
                    <TableHead>Single Ladies</TableHead>
                    <TableHead>Single Males</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>{getDayLabel(template.dayOfWeek)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium capitalize">
                            {template.timeSlot}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatTimeRange(template.startTime, template.endTime)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{formatPrice(template.priceCouples)}</TableCell>
                      <TableCell>{formatPrice(template.priceSingleLadies)}</TableCell>
                      <TableCell>{formatPrice(template.priceSingleMales)}</TableCell>
                      <TableCell>
                        <Switch
                          checked={template.isActive}
                          onCheckedChange={() => handleToggleActive(template)}
                        />
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => handleEdit(template)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(template)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Entry Pass Template</DialogTitle>
            </DialogHeader>
            {renderTemplateForm(true)}
          </DialogContent>
        </Dialog>
      </div>
    </>
  )
}

export default EntryPassTemplatesPage
